import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ICellEditorParams } from 'ag-grid-community';
import {
  deleteOrBackspace,
  finishedEditingPressed,
  isKeyPressedNumericOrDecimal,
  isLeftOrRight,
} from 'utils/validation';

import { KEYS } from 'constants/keyboard';

const { KEY_BACKSPACE, KEY_DELETE } = KEYS;

const NumericCellEditor = forwardRef((props: ICellEditorParams, ref) => {
  const createInitialState = () => {
    let startValue;

    if (props.eventKey === KEY_BACKSPACE || props.eventKey === KEY_DELETE) {
      // if backspace or delete pressed, we clear the cell
      startValue = '';
    } else if (props.charPress) {
      // if a letter was pressed, we start with the letter
      startValue = props.charPress;
    } else {
      // otherwise we start with the current value
      startValue = props.value;
    }

    return {
      value: startValue,
    };
  };

  const initialState = createInitialState();
  const [value, setValue] = useState(initialState.value);
  const refInput = useRef<HTMLInputElement>(null);

  // focus on the input
  useEffect(() => {
    // get ref from React component
    window.setTimeout(() => {
      const eInput = refInput.current!;
      eInput?.focus();
      eInput?.select();
    });
  }, []);

  /* Utility Methods */
  const cancelBeforeStart = props.charPress && '1234567890'.indexOf(props.charPress) < 0;

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isLeftOrRight(event) || deleteOrBackspace(event)) {
      event.stopPropagation();

      return;
    }

    if (!finishedEditingPressed(event) && !isKeyPressedNumericOrDecimal(event)) {
      if (event.preventDefault) event.preventDefault();
    }
  };

  /* Component Editor Lifecycle methods */
  useImperativeHandle(ref, () => {
    return {
      // the final value to send to the grid, on completion of editing
      getValue() {
        const numberFormattedValue = Number(value);

        return isNaN(numberFormattedValue) ? 0 : numberFormattedValue;
      },

      // Gets called once before editing starts, to give editor a chance to
      // cancel the editing before it even starts.
      isCancelBeforeStart() {
        return cancelBeforeStart;
      },

      // Gets called once when editing is finished (eg if Enter is pressed).
      // If you return true, then the result of the edit will be ignored.
      isCancelAfterEnd() {
        // will reject the number if it greater than 999,000,000
        // not very practical, but demonstrates the method.
        return value > 999000000;
      },
    };
  });

  return (
    <input
      type="number"
      ref={refInput}
      className="simple-input-editor"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => onKeyDown(event)}
    />
  );
});

NumericCellEditor.displayName = 'NumericCellEditor';

export default NumericCellEditor;
